// ============================================================================
// PDF EXPORT FOR INTERVIEW ANALYSIS
// ============================================================================

import { jsPDF } from 'jspdf'
import type { Interview, SourceScores, SourceType } from '@/types/interviewer'
import { FACET_TYPE_CONFIG } from '@/types/interviewer'

// Colors matching the app theme
const COLORS = {
  primary: '#1a1a2e',
  text: '#2d3748',
  textMuted: '#718096',
  textSecondary: '#4a5568',
  warm50: '#faf9f7',
  warm100: '#f5f3f0',
  warm200: '#e8e4df',
  solution: '#2d6a4f',
  problem: '#c53030',
  business: '#2b6cb0',
  warning: '#d69e2e',
}

// Score configuration
const SCORE_CONFIG: Record<string, { label: string; criteria: Record<number, string> }> = {
  momTestScore: {
    label: 'Mom Test Score',
    criteria: {
      5: 'Only past behavior, no hypotheticals',
      4: 'Mostly past behavior',
      3: 'Mix of past/future',
      2: 'Mostly hypotheticals',
      1: 'Leading questions',
    },
  },
  evidenceStrength: {
    label: 'Evidence Strength',
    criteria: {
      5: 'Paid money/time already',
      4: 'Active workarounds',
      3: 'Clear frustration',
      2: 'Acknowledges problem',
      1: 'Generic agreement',
    },
  },
  contextRichness: {
    label: 'Context Richness',
    criteria: {
      5: 'Rich context and detail',
      4: 'Good context provided',
      3: 'Adequate context',
      2: 'Limited context',
      1: 'Missing context',
    },
  },
  rationaleQuality: {
    label: 'Rationale Quality',
    criteria: {
      5: 'Full context preserved',
      4: 'Minor gaps',
      3: 'Partial rationale',
      2: 'Vague reasoning',
      1: 'No context',
    },
  },
  contextPreservation: {
    label: 'Context Preservation',
    criteria: {
      5: 'Future team fully understands',
      4: 'Most context captured',
      3: 'Basic understanding',
      2: 'Missing key info',
      1: 'Insufficient',
    },
  },
  alignmentLevel: {
    label: 'Alignment Level',
    criteria: {
      5: 'Full alignment',
      4: 'Minor gaps',
      3: 'Some misalignment',
      2: 'Significant gaps',
      1: 'Major misalignment',
    },
  },
  actionClarity: {
    label: 'Action Clarity',
    criteria: {
      5: 'All actions clear',
      4: 'Most actions defined',
      3: 'Some clarity',
      2: 'Vague actions',
      1: 'No clear actions',
    },
  },
}

function getModeLabel(mode: string): string {
  const labels: Record<string, string> = {
    discovery: 'Discovery Interview',
    decisions: 'Product Decision',
    alignment: 'Team Alignment',
  }
  return labels[mode] || mode
}

function getModeColor(mode: string): string {
  const colors: Record<string, string> = {
    discovery: COLORS.problem,
    decisions: COLORS.solution,
    alignment: COLORS.business,
  }
  return colors[mode] || COLORS.text
}

function getScoreColor(score: number): string {
  if (score >= 4) return COLORS.business
  if (score >= 3) return COLORS.warning
  return COLORS.problem
}

function getRelevantScores(sourceType: SourceType | undefined): string[] {
  switch (sourceType) {
    case 'user_interview':
    case 'buyer_interview':
    case 'support_ticket':
      return ['momTestScore', 'evidenceStrength', 'contextRichness']
    case 'stakeholder_interview':
    case 'meeting_notes':
      return ['alignmentLevel', 'actionClarity', 'contextRichness']
    case 'document':
    case 'issue':
    case 'url':
      return ['evidenceStrength', 'contextRichness', 'rationaleQuality']
    default:
      return ['evidenceStrength', 'contextRichness']
  }
}

export async function exportInterviewToPdf(interview: Interview): Promise<void> {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const margin = 15
  const contentWidth = pageWidth - margin * 2
  let y = margin

  // Helper function to add a new page if needed
  const checkPageBreak = (requiredSpace: number) => {
    if (y + requiredSpace > pageHeight - margin) {
      doc.addPage()
      y = margin
      return true
    }
    return false
  }

  // ============================================================================
  // HEADER
  // ============================================================================

  // Title
  doc.setFontSize(20)
  doc.setTextColor(COLORS.primary)
  doc.setFont('helvetica', 'bold')
  doc.text(interview.title, margin, y)
  y += 8

  // Mode badge
  const modeLabel = getModeLabel(interview.mode)
  const modeColor = getModeColor(interview.mode)
  doc.setFontSize(10)
  doc.setTextColor(modeColor)
  doc.setFont('helvetica', 'normal')
  doc.text(modeLabel, margin, y)
  y += 5

  // Interviewee & date
  if (interview.interviewee) {
    doc.setFontSize(10)
    doc.setTextColor(COLORS.textMuted)
    doc.text(`with ${interview.interviewee}`, margin, y)
    y += 5
  }
  doc.setFontSize(9)
  doc.setTextColor(COLORS.textMuted)
  doc.text(`Analyzed on ${new Date(interview.createdAt).toLocaleDateString()}`, margin, y)
  y += 10

  // Divider
  doc.setDrawColor(COLORS.warm200)
  doc.line(margin, y, pageWidth - margin, y)
  y += 8

  // ============================================================================
  // SCORES SECTION
  // ============================================================================

  const relevantScores = getRelevantScores(interview.sourceType)
  const scoresToShow = relevantScores.filter(key => interview.scores[key as keyof SourceScores] !== undefined)

  if (scoresToShow.length > 0) {
    doc.setFontSize(12)
    doc.setTextColor(COLORS.primary)
    doc.setFont('helvetica', 'bold')
    doc.text('Analysis Scores', margin, y)
    y += 7

    for (const scoreKey of scoresToShow) {
      const value = interview.scores[scoreKey as keyof SourceScores]
      const config = SCORE_CONFIG[scoreKey]
      if (value === undefined || !config) continue

      checkPageBreak(15)

      // Score label
      doc.setFontSize(10)
      doc.setTextColor(COLORS.text)
      doc.setFont('helvetica', 'bold')
      doc.text(config.label, margin, y)

      // Score value
      const scoreColor = getScoreColor(value)
      doc.setTextColor(scoreColor)
      doc.text(`${value}/5`, margin + 60, y)
      y += 4

      // Score bar
      const barWidth = 50
      const barHeight = 3
      doc.setFillColor(COLORS.warm200)
      doc.rect(margin, y, barWidth, barHeight, 'F')
      doc.setFillColor(scoreColor)
      doc.rect(margin, y, (value / 5) * barWidth, barHeight, 'F')
      y += 5

      // Criteria text
      const criteriaText = config.criteria[value]
      if (criteriaText) {
        doc.setFontSize(8)
        doc.setTextColor(COLORS.textMuted)
        doc.setFont('helvetica', 'normal')
        doc.text(criteriaText, margin, y)
        y += 6
      }
    }
    y += 5
  }

  // ============================================================================
  // QUALITY NOTES
  // ============================================================================

  if (interview.qualityNotes.length > 0) {
    checkPageBreak(20)

    doc.setFontSize(12)
    doc.setTextColor(COLORS.primary)
    doc.setFont('helvetica', 'bold')
    doc.text('Quality Notes', margin, y)
    y += 7

    for (const note of interview.qualityNotes) {
      checkPageBreak(10)

      const noteColors: Record<string, string> = {
        warning: COLORS.warning,
        suggestion: COLORS.solution,
        success: COLORS.business,
        error: COLORS.problem,
      }
      const noteColor = noteColors[note.type] || COLORS.textMuted

      doc.setFontSize(9)
      doc.setTextColor(noteColor)
      doc.setFont('helvetica', 'normal')
      const noteLines = doc.splitTextToSize(`• ${note.message}`, contentWidth - 5)
      for (const line of noteLines) {
        doc.text(line, margin + 3, y)
        y += 4
      }
    }
    y += 5
  }

  // ============================================================================
  // EXTRACTED FACETS
  // ============================================================================

  const activeFacets = interview.facets.filter(f => !f.dismissed)

  if (activeFacets.length > 0) {
    checkPageBreak(20)

    doc.setFontSize(12)
    doc.setTextColor(COLORS.primary)
    doc.setFont('helvetica', 'bold')
    doc.text(`Extracted Facets (${activeFacets.length})`, margin, y)
    y += 8

    for (let i = 0; i < activeFacets.length; i++) {
      const facet = activeFacets[i]
      const config = FACET_TYPE_CONFIG[facet.type]
      const facetColor = config?.color || COLORS.textMuted
      const facetLabel = config?.label || facet.type.replace(/_/g, ' ')

      checkPageBreak(35)

      // Facet card background
      doc.setFillColor(COLORS.warm50)
      doc.roundedRect(margin, y, contentWidth, 30, 2, 2, 'F')

      y += 5

      // Facet type badge
      doc.setFontSize(9)
      doc.setTextColor(facetColor)
      doc.setFont('helvetica', 'bold')
      doc.text(facetLabel.toUpperCase(), margin + 3, y)

      // Confidence
      doc.setTextColor(COLORS.textMuted)
      doc.setFont('helvetica', 'normal')
      doc.text(`${Math.round(facet.confidence * 100)}% confidence`, margin + 50, y)
      y += 5

      // Content
      doc.setFontSize(10)
      doc.setTextColor(COLORS.text)
      doc.setFont('helvetica', 'normal')
      const contentLines = doc.splitTextToSize(facet.content, contentWidth - 8)
      for (const line of contentLines.slice(0, 2)) {
        doc.text(line, margin + 3, y)
        y += 4
      }
      if (contentLines.length > 2) {
        doc.text('...', margin + 3, y)
        y += 4
      }

      // Evidence quote
      doc.setFillColor(COLORS.warm100)
      doc.roundedRect(margin + 3, y, contentWidth - 8, 8, 1, 1, 'F')
      // Left border accent
      doc.setFillColor(facetColor)
      doc.rect(margin + 3, y, 1.5, 8, 'F')

      y += 5
      doc.setFontSize(8)
      doc.setTextColor(COLORS.textSecondary)
      doc.setFont('helvetica', 'italic')
      const evidenceLines = doc.splitTextToSize(`"${facet.evidence}"`, contentWidth - 15)
      doc.text(evidenceLines[0] || '', margin + 7, y)
      y += 8

      // Metadata badges
      const badges: string[] = []
      if (facet.severity) badges.push(`${facet.severity} severity`)
      if (facet.frequency) badges.push(facet.frequency)
      if (facet.evidenceStrength) badges.push(`Evidence: ${facet.evidenceStrength}/5`)
      if (facet.decisionType) badges.push(facet.decisionType)
      if (facet.reversibility) badges.push(facet.reversibility === 'one-way-door' ? 'One-way door' : 'Two-way door')
      if (facet.misalignmentPattern) badges.push(facet.misalignmentPattern)

      if (badges.length > 0) {
        doc.setFontSize(7)
        doc.setTextColor(COLORS.textMuted)
        doc.setFont('helvetica', 'normal')
        doc.text(badges.join(' | '), margin + 3, y)
        y += 5
      }

      y += 5
    }
  }

  // ============================================================================
  // TRANSCRIPT (condensed)
  // ============================================================================

  checkPageBreak(30)

  doc.setFontSize(12)
  doc.setTextColor(COLORS.primary)
  doc.setFont('helvetica', 'bold')
  doc.text('Transcript Summary', margin, y)
  y += 7

  doc.setFillColor(COLORS.warm50)
  doc.roundedRect(margin, y, contentWidth, 40, 2, 2, 'F')
  y += 5

  doc.setFontSize(8)
  doc.setTextColor(COLORS.textSecondary)
  doc.setFont('helvetica', 'normal')

  // Truncate transcript to first ~500 chars for summary
  const transcriptPreview = interview.rawContent.slice(0, 800)
  const transcriptLines = doc.splitTextToSize(
    transcriptPreview + (interview.rawContent.length > 800 ? '...' : ''),
    contentWidth - 6
  )

  for (const line of transcriptLines.slice(0, 10)) {
    checkPageBreak(4)
    doc.text(line, margin + 3, y)
    y += 3.5
  }

  if (transcriptLines.length > 10 || interview.rawContent.length > 800) {
    doc.setTextColor(COLORS.textMuted)
    doc.text('[Full transcript continues...]', margin + 3, y)
  }

  // ============================================================================
  // FOOTER
  // ============================================================================

  const totalPages = doc.getNumberOfPages()
  for (let page = 1; page <= totalPages; page++) {
    doc.setPage(page)
    doc.setFontSize(8)
    doc.setTextColor(COLORS.textMuted)
    doc.text(
      `Generated by Intellispot | Page ${page} of ${totalPages}`,
      pageWidth / 2,
      pageHeight - 8,
      { align: 'center' }
    )
  }

  // Save the PDF
  const filename = `${interview.title.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-analysis.pdf`
  doc.save(filename)
}
